/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { GpsProvider, PositionReportSource } from 'src/graphql/generated/graphql.generated';
import {
  CoordinatesRecord,
  CoordinatesProps,
} from 'src/store/generated/coordinates-record.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface PositionReportProps {
  id: string;
  positionTime: Date;
  source: PositionReportSource;
  coordinates: CoordinatesProps | null;
  altitude: number | null;
  speedInKnots: number | null;
  heading: number | null;
  estimatedMinutesEnRoute: number | null;
  fuelRemaining: string | null;
  timezoneCd: string | null;
  dateConfirmed: Date | null;
  gpsProvider: GpsProvider | null;
  tripId: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class PositionReportRecord extends Record<PositionReportProps>({
  id: null as any,
  positionTime: null as any,
  source: null as any,
  coordinates: null as any,
  altitude: null as any,
  speedInKnots: null as any,
  heading: null as any,
  estimatedMinutesEnRoute: null as any,
  fuelRemaining: null as any,
  timezoneCd: null as any,
  dateConfirmed: null as any,
  gpsProvider: null as any,
  tripId: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    positionTime: string | number | Date;
    source: PositionReportSource;
    coordinates: any;
    altitude: number | null;
    speedInKnots: number | null;
    heading: number | null;
    estimatedMinutesEnRoute: number | null;
    fuelRemaining: string | null;
    timezoneCd: string | null;
    dateConfirmed: string | number | Date | null;
    gpsProvider: GpsProvider | null;
    tripId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): PositionReportRecord {
    if (json == null) {
      throw new Error('PositionReportRecord.fromJS: parameter cannot be null');
    }
    return new PositionReportRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'PositionReportJson.id',
      ),
      positionTime: new Date(
        getFromOrThrow(
          json,
          (j) => propValueOrDefault(j, 'positionTime', '0001-01-01T00:00:00.00Z'),
          'PositionReportJson.positionTime',
        ),
      ),
      source: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'source', PositionReportSource.Pilot),
        'PositionReportJson.source',
      ),
      coordinates: json.coordinates != null ? new CoordinatesRecord(json.coordinates) : null,
      altitude: json.altitude,
      speedInKnots: json.speedInKnots,
      heading: json.heading,
      estimatedMinutesEnRoute: json.estimatedMinutesEnRoute,
      fuelRemaining: json.fuelRemaining,
      timezoneCd: json.timezoneCd,
      dateConfirmed: json.dateConfirmed != null ? new Date(json.dateConfirmed) : null,
      gpsProvider: json.gpsProvider,
      tripId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'tripId', '00000000-0000-0000-0000-000000000000'),
        'PositionReportJson.tripId',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'PositionReportJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    positionTime: string | number | Date;
    source: PositionReportSource;
    coordinates: any;
    altitude: number | null;
    speedInKnots: number | null;
    heading: number | null;
    estimatedMinutesEnRoute: number | null;
    fuelRemaining: string | null;
    timezoneCd: string | null;
    dateConfirmed: string | number | Date | null;
    gpsProvider: GpsProvider | null;
    tripId: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [PositionReportRecord, Error[]] {
    if (json == null) {
      throw new Error('PositionReportRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new PositionReportRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'PositionReportJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      positionTime: tryOrDefault(
        () =>
          new Date(
            getFromOrThrow(
              json,
              (j) => propValueOrDefault(j, 'positionTime', '0001-01-01T00:00:00.00Z'),
              'PositionReportJson.positionTime',
            ),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      source: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'source', PositionReportSource.Pilot),
            'PositionReportJson.source',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      coordinates: tryOrDefault(
        () => (json.coordinates != null ? new CoordinatesRecord(json.coordinates) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      altitude: tryOrDefault(
        () => json.altitude,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      speedInKnots: tryOrDefault(
        () => json.speedInKnots,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      heading: tryOrDefault(
        () => json.heading,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      estimatedMinutesEnRoute: tryOrDefault(
        () => json.estimatedMinutesEnRoute,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      fuelRemaining: tryOrDefault(
        () => json.fuelRemaining,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      timezoneCd: tryOrDefault(
        () => json.timezoneCd,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dateConfirmed: tryOrDefault(
        () => (json.dateConfirmed != null ? new Date(json.dateConfirmed) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      gpsProvider: tryOrDefault(
        () => json.gpsProvider,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      tripId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'tripId', '00000000-0000-0000-0000-000000000000'),
            'PositionReportJson.tripId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'PositionReportJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: PositionReportProps) {
    super(props);
    if (props == null) {
      throw new Error('PositionReportRecord constructor parameter cannot be null');
    }
  }
}

export abstract class PositionReportViewModelBase implements PositionReportProps {
  protected readonly _props: PositionReportProps;
  private _json?: PositionReportProps;

  get id(): string {
    return this._props.id;
  }
  get positionTime(): Date {
    return this._props.positionTime;
  }
  get source(): PositionReportSource {
    return this._props.source;
  }
  get coordinates(): CoordinatesProps | null {
    return this._props.coordinates;
  }
  get altitude(): number | null {
    return this._props.altitude;
  }
  get speedInKnots(): number | null {
    return this._props.speedInKnots;
  }
  get heading(): number | null {
    return this._props.heading;
  }
  get estimatedMinutesEnRoute(): number | null {
    return this._props.estimatedMinutesEnRoute;
  }
  get fuelRemaining(): string | null {
    return this._props.fuelRemaining;
  }
  get timezoneCd(): string | null {
    return this._props.timezoneCd;
  }
  get dateConfirmed(): Date | null {
    return this._props.dateConfirmed;
  }
  get gpsProvider(): GpsProvider | null {
    return this._props.gpsProvider;
  }
  get tripId(): string {
    return this._props.tripId;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isGpsAuto() {
    return this._props.source === PositionReportSource.GpsAuto;
  }
  public get isGpsAutoAdsb() {
    return this._props.source === PositionReportSource.GpsAutoAdsb;
  }
  public get isGpsManual() {
    return this._props.source === PositionReportSource.GpsManual;
  }
  public get isPilot() {
    return this._props.source === PositionReportSource.Pilot;
  }
  public get isTCS() {
    return this._props.source === PositionReportSource.TCS;
  }
  public get isMediationService() {
    return this._props.gpsProvider === GpsProvider.MediationService;
  }
  public get isSkytrac() {
    return this._props.gpsProvider === GpsProvider.Skytrac;
  }

  public isAnySource(...positionReportSource: PositionReportSource[]) {
    return positionReportSource.includes(this._props.source);
  }
  public isAnyGpsProvider(...gpsProvider: GpsProvider[]) {
    return this._props.gpsProvider != null && gpsProvider.includes(this._props.gpsProvider);
  }

  constructor(props: PositionReportProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        positionTime: this._props.positionTime,
        source: this._props.source,
        coordinates: this._props.coordinates,
        altitude: this._props.altitude,
        speedInKnots: this._props.speedInKnots,
        heading: this._props.heading,
        estimatedMinutesEnRoute: this._props.estimatedMinutesEnRoute,
        fuelRemaining: this._props.fuelRemaining,
        timezoneCd: this._props.timezoneCd,
        dateConfirmed: this._props.dateConfirmed,
        gpsProvider: this._props.gpsProvider,
        tripId: this._props.tripId,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllPositionReportEntities = (state: AppState) =>
  state.entityStores.positionReport.entities;

export const selectPositionReportById = (id: string) => (state: AppState) =>
  state.entityStores.positionReport.entities.get(id);

export const selectPositionReportMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.positionReport.meta.get(id);

export const selectPositionReportAndMetadataById = (id: string) =>
  createSelector(
    selectPositionReportById(id),
    selectPositionReportMetadataById(id),
    (positionReport, meta) => ({ positionReport, meta }),
  );

export function selectPositionReportEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllPositionReportEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, PositionReportRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectPositionReportEntitiesByPositionTime(positionTime: Date) {
  return createSelector(selectAllPositionReportEntities, (entities) =>
    entities.filter((e) => e.positionTime === positionTime).valueSeq(),
  );
}

export function selectPositionReportEntitiesBySource(source: PositionReportSource) {
  return createSelector(selectAllPositionReportEntities, (entities) =>
    entities.filter((e) => e.source === source).valueSeq(),
  );
}

export function selectPositionReportEntitiesByAltitude(altitude: number) {
  return createSelector(selectAllPositionReportEntities, (entities) =>
    entities.filter((e) => e.altitude === altitude).valueSeq(),
  );
}

export function selectPositionReportEntitiesBySpeedInKnots(speedInKnots: number) {
  return createSelector(selectAllPositionReportEntities, (entities) =>
    entities.filter((e) => e.speedInKnots === speedInKnots).valueSeq(),
  );
}

export function selectPositionReportEntitiesByHeading(heading: number) {
  return createSelector(selectAllPositionReportEntities, (entities) =>
    entities.filter((e) => e.heading === heading).valueSeq(),
  );
}

export function selectPositionReportEntitiesByEstimatedMinutesEnRoute(
  estimatedMinutesEnRoute: number,
) {
  return createSelector(selectAllPositionReportEntities, (entities) =>
    entities.filter((e) => e.estimatedMinutesEnRoute === estimatedMinutesEnRoute).valueSeq(),
  );
}

export function selectPositionReportEntitiesByFuelRemaining(fuelRemaining: string) {
  return createSelector(selectAllPositionReportEntities, (entities) =>
    entities.filter((e) => e.fuelRemaining === fuelRemaining).valueSeq(),
  );
}

export function selectPositionReportEntitiesByTimezoneCd(timezoneCd: string) {
  return createSelector(selectAllPositionReportEntities, (entities) =>
    entities.filter((e) => e.timezoneCd === timezoneCd).valueSeq(),
  );
}

export function selectPositionReportEntitiesByDateConfirmed(dateConfirmed: Date) {
  return createSelector(selectAllPositionReportEntities, (entities) =>
    entities.filter((e) => e.dateConfirmed === dateConfirmed).valueSeq(),
  );
}

export function selectPositionReportEntitiesByGpsProvider(gpsProvider: GpsProvider) {
  return createSelector(selectAllPositionReportEntities, (entities) =>
    entities.filter((e) => e.gpsProvider === gpsProvider).valueSeq(),
  );
}

export function selectPositionReportEntitiesByTripId(tripId: string) {
  return createSelector(selectAllPositionReportEntities, (entities) =>
    entities.filter((e) => e.tripId === tripId).valueSeq(),
  );
}

export function selectPositionReportEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllPositionReportEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
